import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, Plane, PlaneLanding, PlaneTakeoff, Users, Moon, Loader2, Wallet, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { daysBetween } from "../../lib/dates";
import { EditBookingModal } from "./EditBookingModal";
import { NewBookingModal } from "./NewBookingModal";

const DW = 46;       // day column width (px)
const BAR_H = 26;    // booking bar height
const GAP = 4;       // gap between stacked bars
const ROW_PAD = 6;
const WD = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const PALETTE = ["#2F5D62", "#3E5C76", "#5E8B7E", "#4A6FA5", "#6B4F6B", "#795548"];

function iso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function relTime(ts) {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))} min fa`;
  if (diff < 86400) return `${Math.round(diff / 3600)} ore fa`;
  return `${Math.round(diff / 86400)} giorni fa`;
}

export default function AdminCalendar() {
  const [props, setProps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [filterProp, setFilterProp] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const scrollRef = useRef(null);

  const reload = () => Promise.all([api.get("/admin/apartments"), api.get("/admin/bookings")])
    .then(([p, b]) => { setProps(p.data); setBookings(b.data); })
    .catch(() => {})
    .finally(() => setLoading(false));

  const deleteDuplicate = async (b, e) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare questa voce dal calendario?\n\n${b.guest_name} · ${b.checkin} → ${b.checkout}\n\nRimuove SOLO il duplicato dal sito: non cancella nulla su Smoobu e non invia email all'ospite.`)) return;
    try {
      await api.delete(`/admin/bookings/${b.id}/local`);
      toast.success("Duplicato eliminato dal calendario");
      setBookings((prev) => prev.filter((x) => x.id !== b.id));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => { reload(); }, []);

  // Only ACTIVE structures are shown on the calendar
  const activeProps = useMemo(() => props.filter((p) => p.active !== false), [props]);

  const start = month;
  const DAYS = 42;
  const daysArr = useMemo(() => {
    const arr = [];
    for (let i = 0; i < DAYS; i++) {
      const d = new Date(start); d.setDate(d.getDate() + i);
      arr.push({ date: d, iso: iso(d), dow: d.getDay(), day: d.getDate(), month: d.getMonth(), firstOfMonth: d.getDate() === 1, weekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    return arr;
  }, [start]);

  // Build rows: one per apartment, one per hotel room type
  const rows = useMemo(() => {
    const r = [];
    activeProps.forEach((p) => {
      if (filterProp && p.id !== filterProp) return;
      if (p.type === "hotel") {
        (p.room_types || []).forEach((rt) => r.push({ key: `${p.id}:${rt.id}`, propId: p.id, roomId: rt.id, label: p.title, sub: rt.name, hotel: true }));
      } else {
        r.push({ key: `${p.id}`, propId: p.id, roomId: null, label: p.label || p.title, sub: "Appartamento", hotel: false });
      }
    });
    return r;
  }, [activeProps, filterProp]);

  const startIso = daysArr[0]?.iso;
  const endIso = daysArr[DAYS - 1]?.iso;

  // Group bookings into rows with lane packing
  const rowData = useMemo(() => {
    const active = bookings.filter((b) => b.status !== "cancelled");
    return rows.map((row) => {
      const items = active
        .filter((b) => (b.room_type_id ? `${b.apartment_id}:${b.room_type_id}` : `${b.apartment_id}`) === row.key)
        .map((b) => ({ ...b, ciIdx: daysBetween(startIso, b.checkin), coIdx: daysBetween(startIso, b.checkout) }))
        .filter((b) => b.coIdx > 0 && b.ciIdx < DAYS)
        .sort((a, c) => a.ciIdx - c.ciIdx);
      // lane packing
      const laneEnds = [];
      items.forEach((it) => {
        let placed = false;
        for (let i = 0; i < laneEnds.length; i++) {
          if (it.ciIdx >= laneEnds[i]) { it.lane = i; laneEnds[i] = it.coIdx; placed = true; break; }
        }
        if (!placed) { it.lane = laneEnds.length; laneEnds.push(it.coIdx); }
      });
      return { ...row, items, lanes: Math.max(1, laneEnds.length) };
    });
  }, [rows, bookings, startIso]);

  // Upcoming events (check-in / check-out) from today
  const today = iso(new Date());
  const upcoming = useMemo(() => {
    const ev = [];
    bookings.filter((b) => b.status !== "cancelled").forEach((b) => {
      if (b.checkin >= today) ev.push({ type: "in", date: b.checkin, b });
      if (b.checkout >= today) ev.push({ type: "out", date: b.checkout, b });
    });
    return ev.sort((a, c) => (a.date < c.date ? -1 : a.date > c.date ? 1 : 0)).slice(0, 6);
  }, [bookings]);

  const recent = useMemo(() =>
    [...bookings].sort((a, c) => (a.created_at < c.created_at ? 1 : -1)).slice(0, 5), [bookings]);

  const gridW = DAYS * DW;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-fir" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl text-fir flex items-center gap-2"><CalendarDays className="text-gold" /> Calendario</h1>
          <p className="text-fir-deep/60">Solo strutture attive. Clicca una prenotazione per modificarla, o una cella libera per crearne una nuova. Passa il mouse su una barra e clicca ✕ per eliminare un duplicato (solo dal sito).</p>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="calendar-filter" value={filterProp} onChange={(e) => setFilterProp(e.target.value)} className="input-alpine">
            <option value="">Tutte le strutture attive</option>
            {activeProps.map((p) => <option key={p.id} value={p.id}>{p.type === "hotel" ? "🏨 " : "🏠 "}{p.title}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-2 rounded-lg border border-stone text-fir hover:bg-stone/50"><ChevronLeft size={18} /></button>
            <span data-testid="cal-month" className="font-display text-lg text-fir capitalize min-w-[140px] text-center">
              {month.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
            </span>
            <button data-testid="cal-next" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-2 rounded-lg border border-stone text-fir hover:bg-stone/50"><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>

      {/* CALENDAR TIMELINE */}
      <div className="bg-white rounded-2xl border border-stone overflow-hidden mb-8" data-testid="booking-calendar">
        <div className="flex">
          {/* Left labels column */}
          <div className="w-52 shrink-0 border-r border-stone bg-white relative z-20 overflow-hidden" style={{ boxShadow: "2px 0 6px rgba(0,0,0,0.05)" }}>
            <div className="h-12 border-b border-stone flex items-center px-4 text-xs uppercase tracking-wide text-fir-deep/50">Struttura</div>
            {rowData.map((row) => (
              <div key={row.key} className="border-b border-stone px-4 flex flex-col justify-center" style={{ height: row.lanes * (BAR_H + GAP) + ROW_PAD * 2 }}>
                <span className="text-sm font-medium text-fir truncate">{row.label}</span>
                <span className="text-xs text-fir-deep/50 truncate">{row.sub}</span>
              </div>
            ))}
            {rowData.length === 0 && <div className="px-4 py-6 text-sm text-fir-deep/50">Nessuna struttura.</div>}
          </div>

          {/* Scrollable day grid */}
          <div className="overflow-x-auto flex-1 min-w-0" ref={scrollRef}>
            <div style={{ width: gridW }}>
              {/* header */}
              <div className="flex h-12 border-b border-stone">
                {daysArr.map((d) => (
                  <div key={d.iso} style={{ width: DW }}
                    className={`shrink-0 flex flex-col items-center justify-center border-r border-stone/60 ${d.weekend ? "bg-stone/40" : ""}`}>
                    {d.firstOfMonth && <span className="text-[9px] uppercase text-gold font-bold leading-none">{MONTHS[d.month]}</span>}
                    <span className="text-[10px] text-fir-deep/50 leading-none">{WD[d.dow]}</span>
                    <span className={`text-sm ${d.iso === today ? "text-ivory bg-fir rounded-full w-6 h-6 flex items-center justify-center mt-0.5" : "text-fir-deep"}`}>{d.day}</span>
                  </div>
                ))}
              </div>
              {/* rows */}
              {rowData.map((row) => (
                <div key={row.key} className="relative border-b border-stone" style={{ height: row.lanes * (BAR_H + GAP) + ROW_PAD * 2 }}>
                  {/* day cell backgrounds (clickable to create a booking) */}
                  <div className="absolute inset-0 flex">
                    {daysArr.map((d) => (
                      <div key={d.iso} style={{ width: DW }}
                        onClick={() => setCreating({ apartment_id: row.propId, room_type_id: row.roomId, checkin: d.iso })}
                        title={`Nuova prenotazione · ${d.iso}`}
                        data-testid="calendar-empty-cell"
                        className={`border-r border-stone/40 cursor-pointer hover:bg-gold/15 transition-colors ${d.weekend ? "bg-stone/20" : ""}`} />
                    ))}
                  </div>
                  {/* booking bars */}
                  {row.items.map((b, i) => {
                    const barStart = Math.max(b.ciIdx + 0.5, 0);
                    const barEnd = Math.min(b.coIdx + 0.5, DAYS);
                    const left = barStart * DW;
                    const width = Math.max((barEnd - barStart) * DW, 14);
                    const color = b.status === "pending" ? "#C68B59" : PALETTE[i % PALETTE.length];
                    return (
                      <div key={b.id} data-testid="calendar-booking-bar" title={`${b.guest_name} · ${b.checkin} → ${b.checkout}`}
                        onClick={(e) => { e.stopPropagation(); setEditing(b); }}
                        style={{ left, width, top: ROW_PAD + b.lane * (BAR_H + GAP), height: BAR_H, background: color }}
                        className="group absolute rounded-full flex items-center px-3 text-ivory text-xs font-medium shadow-sm overflow-hidden whitespace-nowrap cursor-pointer hover:brightness-110 transition-all">
                        {b.status === "pending" && <span className="mr-1">⏳</span>}
                        <span className="truncate">{b.guest_name}</span>
                        <button data-testid="calendar-delete-duplicate" title="Elimina duplicato (solo dal sito, non tocca Smoobu)"
                          onClick={(e) => deleteDuplicate(b, e)}
                          className="ml-auto shrink-0 opacity-0 group-hover:opacity-100 hover:bg-black/25 rounded-full p-0.5 transition-opacity">
                          <X size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM PANELS */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-stone p-6">
          <h2 className="font-display text-2xl text-fir mb-4">Prossimi eventi</h2>
          {upcoming.length === 0 && <p className="text-sm text-fir-deep/50">Nessun evento in arrivo.</p>}
          <div className="space-y-3">
            {upcoming.map((e, i) => (
              <div key={i} data-testid="upcoming-event" className="rounded-xl border border-stone p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.type === "in" ? "bg-green-100 text-green-700" : "bg-stone text-fir-deep"}`}>
                    {e.type === "in" ? "Check-in" : "Check-out"}
                  </span>
                  <span className="flex items-center gap-2 text-fir-deep/70">
                    {e.type === "in" ? <PlaneLanding size={15} /> : <PlaneTakeoff size={15} />}
                    {new Date(e.date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}
                    <span className="flex items-center gap-1"><Users size={13} />{e.b.guests ?? e.b.adults}</span>
                    <span className="flex items-center gap-1"><Moon size={13} />{e.b.num_nights}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-fir-deep">
                  <Users size={14} className="text-wood" /> {e.b.guest_name}
                  <span className="text-fir-deep/40">·</span> {e.b.apartment_title}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone p-6">
          <h2 className="font-display text-2xl text-fir mb-4">Ultime attività</h2>
          {recent.length === 0 && <p className="text-sm text-fir-deep/50">Nessuna prenotazione registrata.</p>}
          <div className="space-y-3">
            {recent.map((b) => (
              <div key={b.id} data-testid="recent-activity" className="rounded-xl border border-stone p-3">
                <div className="flex items-center justify-between">
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">Nuova prenotazione</span>
                  <span className="flex items-center gap-3 text-sm text-fir-deep/70">
                    <span className="flex items-center gap-1"><Wallet size={14} />€{b.total}</span>
                    <span className="flex items-center gap-1"><Moon size={13} />{b.num_nights}</span>
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="flex items-center gap-2 text-fir-deep"><Users size={14} className="text-wood" />{b.guest_name}</span>
                  <span className="text-xs text-fir-deep/50">{relTime(b.created_at)}</span>
                </div>
                <div className="text-sm text-fir-deep/60 mt-0.5">{b.apartment_title} · arrivo {b.checkin}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {editing && <EditBookingModal booking={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {creating && <NewBookingModal properties={props} initial={creating} onClose={() => setCreating(null)} onSaved={() => { setCreating(null); reload(); }} />}
    </div>
  );
}
